import LinkOff from '@mui/icons-material/LinkOff';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmSheet } from '../../components/ConfirmSheet';
import type { ApiError } from '../../lib/api/api-client';

import { ReasonAlert } from './ReasonAlert';

/** Props of {@link DisconnectSheet}. */
export interface DisconnectSheetProps {
  readonly open: boolean;
  /** Open FXMate positions; null while `GET /me` has not answered (the sentence is left out, P5). */
  readonly positions: number | null;
  readonly busy: boolean;
  readonly error: ApiError | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * The disconnect confirmation (S8 R8): copying stops on every channel until an account is
 * connected, the positions stay open at the broker (Q85), and a new account needs a new
 * authorization (S8 R9). Texts provisional (no upstream wording).
 */
export function DisconnectSheet({
  open,
  positions,
  busy,
  error,
  onConfirm,
  onCancel,
}: DisconnectSheetProps): ReactElement {
  const { t } = useTranslation();
  return (
    <ConfirmSheet
      open={open}
      title={t('account.disconnectSheet.title')}
      confirmLabel={t('account.disconnectSheet.confirm')}
      cancelLabel={t('account.cancel')}
      icon={<LinkOff />}
      busy={busy}
      footer={error === null ? undefined : <ReasonAlert error={error} />}
      onConfirm={() => {
        onConfirm();
      }}
      onCancel={onCancel}
    >
      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 600 }}>{t('account.disconnectSheet.stops')}</Typography>
        {positions !== null && (
          <Typography>{t('account.openPositions', { count: positions })}</Typography>
        )}
        <Typography>{t('account.disconnectSheet.consent')}</Typography>
      </Stack>
    </ConfirmSheet>
  );
}
